import type { FC, ReactNode } from 'react'
import type { MessageTemplateKey } from '@/types'

import { useEffect, useId, useState } from 'react'
import { Copy, MessageCircle } from 'lucide-react'
import { Button } from '@/components/kit/button'
import { Sheet } from '@/components/kit/sheet'
import { useToast } from '@/components/kit/toast'
import { Segmented } from '@/components/kit/segmented'
import { Field, Input, Textarea } from '@/components/kit/field'
import { useCarWash } from '@/hooks/use-carwash'
import { useCarWashActions } from '@/hooks/use-carwash-actions'
import { MESSAGE_TEMPLATES } from '@/constants/carwash'
import { copyText } from '@/lib/clipboard'
import { formatMoney } from '@/lib/carwash/format'
import { formatPhone, samePhone, toWhatsAppNumber } from '@/lib/carwash/phone'
import { plateLabel } from '@/lib/carwash/plate'
import { fillTemplate, whatsappLinkChannel } from '@/lib/carwash/whatsapp'

export type WhatsAppTarget = {
    jobId: string | null
    invoiceId: string | null
    template: MessageTemplateKey
}

interface WhatsAppSheetProps {
    target: WhatsAppTarget | null
    open: boolean
    onClose: () => void
}

/*
 * Prepares a customer message from the saved templates. It opens WhatsApp
 * with the text filled in; the operator presses send there. Nothing is sent
 * automatically until a WhatsApp Business API connection is added.
 */
export const WhatsAppSheet: FC<WhatsAppSheetProps> = ({ target, open, onClose }): ReactNode => {
    const { state, lookups } = useCarWash()
    const actions = useCarWashActions()
    const toast = useToast()
    const phoneId = useId()
    const messageId = useId()

    const [template, setTemplate] = useState<MessageTemplateKey>('received')
    const [phone, setPhone] = useState('')
    const [body, setBody] = useState('')
    const [savePhone, setSavePhone] = useState(true)
    const [touched, setTouched] = useState(false)

    const invoice = target?.invoiceId && lookups ? lookups.invoices.get(target.invoiceId) ?? null : null
    const job = lookups
        ? (target?.jobId ? lookups.jobs.get(target.jobId) ?? null : invoice ? lookups.jobs.get(invoice.jobId) ?? null : null)
        : null
    const vehicle = lookups && (job || invoice) ? lookups.vehicles.get(job?.vehicleId ?? invoice?.vehicleId ?? '') ?? null : null
    const customerId = job?.customerId ?? invoice?.customerId ?? vehicle?.customerId ?? null
    const customer = customerId && lookups ? lookups.customers.get(customerId) ?? null : null

    const compose = (key: MessageTemplateKey): string => {
        if (!state) return ''

        const currency = state.settings.currency
        const plate = job ? plateLabel(job.plate, `#${job.number}`) : invoice ? plateLabel(invoice.plate, 'your vehicle') : 'your vehicle'

        return fillTemplate(state.settings.templates[key], {
            vehicle: plate,
            model: job?.vehicleModel ?? invoice?.vehicleModel ?? '',
            service: job?.serviceName ?? invoice?.serviceName ?? 'your service',
            amount: invoice ? formatMoney(invoice.total, currency) : job ? formatMoney(job.price, currency) : '',
            invoice: invoice?.number ?? '',
            customer: customer?.name ?? '',
            business: state.settings.name
        })
    }

    useEffect(() => {
        if (!open || !target) return

        setTemplate(target.template)
        setPhone(customer?.phone ? formatPhone(customer.phone) : '')
        setBody(compose(target.template))
        setSavePhone(true)
        setTouched(false)
        // Prefill only when the sheet opens for a new vehicle or bill.
    }, [open, target]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!state) return null

    const whatsappNumber = toWhatsAppNumber(phone, state.settings.countryCode)
    const phoneError = touched && phone.trim() && !whatsappNumber ? 'Enter a valid mobile number.' : null
    const canSave = Boolean(vehicle && whatsappNumber && !samePhone(customer?.phone, phone))
    const plate = job ? plateLabel(job.plate, `#${job.number}`) : invoice ? plateLabel(invoice.plate) : 'this vehicle'

    const changeTemplate = (key: MessageTemplateKey) => {
        setTemplate(key)
        setBody(compose(key))
    }

    const openWhatsApp = () => {
        setTouched(true)
        if (!whatsappNumber || !body.trim()) return

        if (canSave && savePhone && vehicle) {
            actions.updateVehicle(vehicle.id, {
                model: vehicle.model,
                ownerName: customer?.name ?? null,
                ownerPhone: phone
            })
        }

        actions.logMessage({
            jobId: job?.id ?? null,
            invoiceId: invoice?.id ?? null,
            customerId,
            phone: whatsappNumber,
            template,
            body: body.trim()
        })

        void whatsappLinkChannel.send({ phone: whatsappNumber, text: body.trim() })

        toast({ title: 'WhatsApp opened with your message', description: 'Tap send in WhatsApp to deliver it.' })
        onClose()
    }

    const copy = async () => {
        const copied = await copyText(body.trim())
        toast(copied ? { title: 'Message copied' } : { title: 'Could not copy the message', tone: 'error' })
    }

    return (
        <Sheet
            open={open}
            onClose={onClose}
            title='WhatsApp message'
            description={`For ${plate}${customer?.name ? `, ${customer.name}` : ''}`}
            footer={(
                <div className='space-y-2.5'>
                    <div className='grid grid-cols-[auto_1fr] gap-3'>
                        <Button variant='secondary' size='lg' onClick={copy} disabled={!body.trim()}>
                            <Copy aria-hidden='true' />
                            Copy
                        </Button>

                        <Button variant='success' size='lg' onClick={openWhatsApp} disabled={!body.trim()}>
                            <MessageCircle aria-hidden='true' />
                            Open WhatsApp
                        </Button>
                    </div>

                    <p className='text-center text-xs text-muted-foreground'>
                        WhatsApp opens with this message ready. Tap send there to deliver it.
                    </p>
                </div>
            )}
        >
            <div className='space-y-4'>
                <Segmented
                    label='Message type'
                    size='sm'
                    value={template}
                    onChange={changeTemplate}
                    options={MESSAGE_TEMPLATES.map(item => ({ value: item.key, label: item.label }))}
                />

                <Field
                    label='Customer mobile number'
                    htmlFor={phoneId}
                    error={phoneError}
                    hint={`Numbers without a country code use +${state.settings.countryCode}.`}
                >
                    <Input
                        id={phoneId}
                        type='tel'
                        inputMode='tel'
                        autoComplete='off'
                        placeholder='98470 12345'
                        value={phone}
                        onChange={event => setPhone(event.target.value)}
                        onBlur={() => setTouched(true)}
                        aria-invalid={Boolean(phoneError)}
                    />
                </Field>

                {canSave && (
                    <label className='flex items-center gap-2.5 text-sm text-foreground'>
                        <input
                            type='checkbox'
                            checked={savePhone}
                            onChange={event => setSavePhone(event.target.checked)}
                            className='size-[18px] rounded border-input accent-[var(--color-primary)]'
                        />
                        {`Save this number for ${plate}`}
                    </label>
                )}

                <Field label='Message' htmlFor={messageId}>
                    <Textarea
                        id={messageId}
                        value={body}
                        onChange={event => setBody(event.target.value)}
                        className='min-h-40'
                    />
                </Field>
            </div>
        </Sheet>
    )
}
